/**
 * M6.5-W9 — ArchiMate 3.2 native symbol shapes voor canvas-rendering.
 *
 * Wanneer een concept `attributes.notation_style === "archimate"` heeft
 * EN geen stereotype is toegepast EN het base-type een entry in deze
 * library heeft, rendert ConceptNode het concept in z'n ArchiMate-3.2-
 * notatie i.p.v. een rechthoek met corner-icon.
 *
 * Aanpak:
 *  - **Polygonale shapes** (Process-arrow, Function-chevron, Event-notch,
 *    Role-notch, Product-corner) → CSS `clip-path` op de wrapper. Snel,
 *    pixel-veilig bij resize, NodeResizer + edge-drag-zones blijven werken.
 *  - **Complexe shapes** (Actor-figurine, Collaboration-cirkels, Interface-
 *    lollipop, Interaction-yin-yang) → inline `<svg>` als decoratie-laag
 *    achter het label. Wrapper-bg wordt transparant; de SVG-paths
 *    schilderen de echte shape.
 *  - **Subtle shapes** (Object-bar, Contract-bars, Service-rounded) →
 *    CSS-only tweaks (borderRadius, `::before`).
 *
 * Eerste iteratie dekt de Business-laag (13 types). Application /
 * Technology / Motivation / Strategy / Implementation volgen.
 */
import type { CSSProperties, ReactNode } from "react";

export interface ArchimateShapeStyle {
  /** CSS clip-path expressie voor de wrapper. Vormt de buitenrand van
   *  de node. Niet zetten = rechthoekige wrapper (voor shapes die alleen
   *  een SVG-overlay nodig hebben). */
  clipPath?: string;
  /** Extra border-radius op de wrapper. Bv. 18px voor BusinessService,
   *  of "50%" voor een echte ellipse (Value). */
  borderRadius?: number | string;
  /** Decoratie-SVG die als absolute-positioned overlay binnen de wrapper
   *  rendert. Schildert de echte shape voor complexe vormen waar
   *  clip-path tekortschiet. Ontvangt fill/stroke uit ConceptNode. */
  Decoration?: (props: { fill: string; stroke: string }) => ReactNode;
  /** True voor shapes waarbij de SVG-decoratie HET symbool IS — geen
   *  omringende rechthoek meer. Actor / Collaboration / Interface /
   *  Interaction zijn iconic; de wrapper wordt transparant en de
   *  corner-icon verbergen we (Decoration bedekt 't centrum). Label
   *  vloeit eronder dankzij de bestaande flex-layout. */
  iconic?: boolean;
  /** Default wrapper-grootte (px) bij toggle naar archimate-symbool —
   *  per type een passende verhouding (Actor 40×64 smal+hoog, Role-
   *  cilinder 100×40 breed+laag, etc.). Wordt door AttributePanel
   *  gebruikt om resizeNodeOnCanvas mee te triggeren. */
  defaultSize?: { width: number; height: number };
  /** True voor shapes die als dashed rectangle renderen (Grouping). */
  dashedBorder?: boolean;
  /** SVG `<polygon>`-points-string die de stroke-outline van de
   *  clip-path-shape rendert. ConceptNode plaatst hierop een absolute-
   *  positioned `<svg>` met `<polygon stroke="..." fill="none">` zodat
   *  ook schuine kanten een rand krijgen (CSS-box-shadow-inset
   *  respecteert clip-path niet). */
  outlinePoints?: string;
  /** Padding-toevoeging zodat het label binnen de zichtbare shape valt
   *  (bv. bij arrow-shapes met punt rechts moet de tekst niet onder de
   *  punt verdwijnen). */
  contentPadding?: { top?: number; right?: number; bottom?: number; left?: number };
}

/** Polygon helper: bouwt een `polygon(...)`-clip-path string uit een
 *  array van [x%, y%]-paren. */
function poly(points: ReadonlyArray<readonly [number, number]>): string {
  return `polygon(${points.map(([x, y]) => `${x}% ${y}%`).join(", ")})`;
}

/** Idem maar als SVG-`points`-string voor `<polygon>`. Gebruikt door
 *  ConceptNode om een stroke-outline te schilderen die de clip-path-
 *  edges volgt — CSS box-shadow inset wordt afgesneden door clip-path
 *  en geeft daarom geen rand op schuine kanten. */
function svgPoints(points: ReadonlyArray<readonly [number, number]>): string {
  return points.map(([x, y]) => `${x},${y}`).join(" ");
}

/** Helper: bouwt clipPath + outlinePoints uit één punten-array zodat ze
 *  altijd in sync zijn. */
function polyShape(points: ReadonlyArray<readonly [number, number]>): {
  clipPath: string;
  outlinePoints: string;
} {
  return { clipPath: poly(points), outlinePoints: svgPoints(points) };
}

/* ──────────────────────── Business-laag shapes ─────────────────────── */

/** BusinessProcess: brede pijl naar rechts — rechthoekige staart op
 *  midden-hoogte, met arrowhead die boven en onder de staart uitsteekt
 *  en uitloopt in een punt rechts. */
const PROCESS_ARROW = polyShape([
  [0, 25],
  [65, 25],
  [65, 0],
  [100, 50],
  [65, 100],
  [65, 75],
  [0, 75],
]);

/** Business/Application/Technology Function: rechthoek met W-patroon
 *  op de TOP edge — twee peaks met V-valley tussen. Bottom is vlak.
 *  De peaks staan op ~25% en ~75% breedte; corners en valley liggen
 *  ~25% naar beneden. */
const FUNCTION_CHEVRON = polyShape([
  [0, 25],
  [25, 0],
  [50, 25],
  [75, 0],
  [100, 25],
  [100, 100],
  [0, 100],
]);

/** BusinessEvent: rechthoek met concave-left edge (dipt naar binnen
 *  in 't midden) en arrowhead-punt rechts. Lijkt op een vissenstaart-
 *  rocket-vorm. Anders dan Process: geen aparte staart, alleen body. */
const EVENT_PENTAGON = polyShape([
  [0, 0],
  [78, 0],
  [100, 50],
  [78, 100],
  [0, 100],
  [12, 50],
]);

/** ValueStream: chevron-arrow met diepere concave-left notch en meer
 *  elongated body — verschil met Event: scherper en chevron-achtiger. */
const VALUE_STREAM_CHEVRON = polyShape([
  [0, 0],
  [70, 0],
  [100, 50],
  [70, 100],
  [0, 100],
  [25, 50],
]);

/** Iconic-decoraties — vullen de bovenste 65% van de wrapper, label valt
 *  in de bottom-35% via flex-flow. Stroke gebruikt currentColor of expliciet. */
const ICONIC_SVG_STYLE: CSSProperties = {
  position: "absolute",
  // Vul 95% van wrapper-breedte zodat de symbol-randen vrijwel
  // samenvallen met de wrapper-randen — edge-attachment landt dan
  // visueel direct op het symbool i.p.v. met 7-8 px wrapper-gap.
  // Hoogte 75% laat ruimte voor label onderaan.
  top: 2,
  left: "50%",
  transform: "translateX(-50%)",
  width: "95%",
  height: "75%",
  pointerEvents: "none",
};

/** Generieke "Actor-figurine"-decoratie: hoofd-cirkel + romp-stokjes. */
const ActorDecoration = ({ stroke }: { fill: string; stroke: string }): ReactNode => (
  <svg viewBox="0 0 60 60" style={ICONIC_SVG_STYLE} aria-hidden preserveAspectRatio="xMidYMid meet">
    <circle cx="30" cy="14" r="8" fill="none" stroke={stroke} strokeWidth="2" />
    <line x1="30" y1="22" x2="30" y2="42" stroke={stroke} strokeWidth="2" />
    <line x1="14" y1="30" x2="46" y2="30" stroke={stroke} strokeWidth="2" />
    <line x1="30" y1="42" x2="18" y2="56" stroke={stroke} strokeWidth="2" />
    <line x1="30" y1="42" x2="42" y2="56" stroke={stroke} strokeWidth="2" />
  </svg>
);

/** Collaboration: twee overlappende cirkels. */
const CollaborationDecoration = ({ stroke }: { fill: string; stroke: string }): ReactNode => (
  <svg viewBox="0 0 60 30" style={ICONIC_SVG_STYLE} aria-hidden preserveAspectRatio="xMidYMid meet">
    <circle cx="22" cy="15" r="12" fill="none" stroke={stroke} strokeWidth="2" />
    <circle cx="38" cy="15" r="12" fill="none" stroke={stroke} strokeWidth="2" />
  </svg>
);

/** Interface: lollipop (lijn + open cirkel). */
const InterfaceDecoration = ({ stroke }: { fill: string; stroke: string }): ReactNode => (
  <svg viewBox="0 0 60 30" style={ICONIC_SVG_STYLE} aria-hidden preserveAspectRatio="xMidYMid meet">
    <line x1="6" y1="15" x2="38" y2="15" stroke={stroke} strokeWidth="2" />
    <circle cx="46" cy="15" r="10" fill="none" stroke={stroke} strokeWidth="2" />
  </svg>
);

/** Interaction: cirkel met verticale streep door 't midden. */
const InteractionDecoration = ({ fill, stroke }: { fill: string; stroke: string }): ReactNode => (
  <svg viewBox="0 0 60 60" style={ICONIC_SVG_STYLE} aria-hidden preserveAspectRatio="xMidYMid meet">
    <circle cx="30" cy="30" r="26" fill={fill} stroke={stroke} strokeWidth="2" />
    <line x1="30" y1="4" x2="30" y2="56" stroke={stroke} strokeWidth="2" />
  </svg>
);

/** Role / Stakeholder: horizontale cilinder — twee ellipsen (caps) +
 *  twee horizontale body-lijnen. Geen rechthoek-rand zodat de linker-
 *  ellips niet doormidden gesneden wordt door de rechthoek-edge. */
const RoleDecoration = ({ fill, stroke }: { fill: string; stroke: string }): ReactNode => (
  <svg viewBox="0 0 80 40" style={ICONIC_SVG_STYLE} aria-hidden preserveAspectRatio="xMidYMid meet">
    {/* Body fill (transparante rect zonder stroke, alleen voor fill-area) */}
    <rect x="10" y="2" width="60" height="36" fill={fill} stroke="none" />
    {/* Linker cap — volle ellips */}
    <ellipse cx="10" cy="20" rx="6" ry="18" fill={fill} stroke={stroke} strokeWidth="2" />
    {/* Rechter cap — volle ellips */}
    <ellipse cx="70" cy="20" rx="6" ry="18" fill={fill} stroke={stroke} strokeWidth="2" />
    {/* Body-lijnen (top + bottom) — verbindt de twee caps */}
    <line x1="10" y1="2" x2="70" y2="2" stroke={stroke} strokeWidth="2" />
    <line x1="10" y1="38" x2="70" y2="38" stroke={stroke} strokeWidth="2" />
  </svg>
);

/** BusinessObject: rechthoek met dunne lijn aan de bovenkant. */
const ObjectDecoration = ({ stroke }: { fill: string; stroke: string }): ReactNode => (
  <div
    className="pointer-events-none absolute top-0 right-0 left-0"
    style={{ height: 3, borderTop: `2px solid ${stroke}`, marginTop: 4 }}
    aria-hidden
  />
);

/** Contract: BusinessObject + één extra lijn iets lager. */
const ContractDecoration = ({ stroke }: { fill: string; stroke: string }): ReactNode => (
  <>
    <div
      className="pointer-events-none absolute top-0 right-0 left-0"
      style={{ height: 3, borderTop: `2px solid ${stroke}`, marginTop: 4 }}
      aria-hidden
    />
    <div
      className="pointer-events-none absolute top-0 right-0 left-0"
      style={{ height: 3, borderTop: `1px solid ${stroke}`, marginTop: 9 }}
      aria-hidden
    />
  </>
);

/* ──────────────────────── Technology iconic shapes ────────────────── */

/** Node: isometrische 3D-kubus. */
const NodeDecoration = ({ fill, stroke }: { fill: string; stroke: string }): ReactNode => (
  <svg viewBox="0 0 80 60" style={ICONIC_SVG_STYLE} aria-hidden preserveAspectRatio="xMidYMid meet">
    {/* Front face */}
    <polygon points="10,20 60,20 60,55 10,55" fill={fill} stroke={stroke} strokeWidth="2" />
    {/* Top face */}
    <polygon points="10,20 25,5 75,5 60,20" fill={fill} stroke={stroke} strokeWidth="2" />
    {/* Right face */}
    <polygon points="60,20 75,5 75,40 60,55" fill={fill} stroke={stroke} strokeWidth="2" />
  </svg>
);

/** Device: schermpje op standaard (monitor). */
const DeviceDecoration = ({ fill, stroke }: { fill: string; stroke: string }): ReactNode => (
  <svg viewBox="0 0 80 50" style={ICONIC_SVG_STYLE} aria-hidden preserveAspectRatio="xMidYMid meet">
    <rect x="5" y="5" width="70" height="32" fill={fill} stroke={stroke} strokeWidth="2" />
    {/* Standaard */}
    <line x1="40" y1="37" x2="40" y2="45" stroke={stroke} strokeWidth="2" />
    <line x1="22" y1="45" x2="58" y2="45" stroke={stroke} strokeWidth="2" />
  </svg>
);

/** SystemSoftware: cirkel/bol. */
const SystemSoftwareDecoration = ({
  fill,
  stroke,
}: {
  fill: string;
  stroke: string;
}): ReactNode => (
  <svg viewBox="0 0 60 60" style={ICONIC_SVG_STYLE} aria-hidden preserveAspectRatio="xMidYMid meet">
    <circle cx="30" cy="30" r="26" fill={fill} stroke={stroke} strokeWidth="2" />
  </svg>
);

/** Path: dubbele pijl met streep — `<- - ->`. */
const PathDecoration = ({ stroke }: { fill: string; stroke: string }): ReactNode => (
  <svg viewBox="0 0 80 30" style={ICONIC_SVG_STYLE} aria-hidden preserveAspectRatio="xMidYMid meet">
    {/* Linker pijl */}
    <line x1="20" y1="15" x2="6" y2="15" stroke={stroke} strokeWidth="2" />
    <polyline points="12,9 6,15 12,21" fill="none" stroke={stroke} strokeWidth="2" />
    {/* Streep midden */}
    <line x1="32" y1="15" x2="48" y2="15" stroke={stroke} strokeWidth="2" strokeDasharray="4 2" />
    {/* Rechter pijl */}
    <line x1="60" y1="15" x2="74" y2="15" stroke={stroke} strokeWidth="2" />
    <polyline points="68,9 74,15 68,21" fill="none" stroke={stroke} strokeWidth="2" />
  </svg>
);

/** CommunicationNetwork: 4 verbonden knopen (graph-icoon). */
const CommunicationNetworkDecoration = ({
  stroke,
}: {
  fill: string;
  stroke: string;
}): ReactNode => (
  <svg viewBox="0 0 60 60" style={ICONIC_SVG_STYLE} aria-hidden preserveAspectRatio="xMidYMid meet">
    {/* Verbindingen */}
    <line x1="12" y1="20" x2="48" y2="20" stroke={stroke} strokeWidth="1.5" />
    <line x1="12" y1="20" x2="30" y2="48" stroke={stroke} strokeWidth="1.5" />
    <line x1="48" y1="20" x2="30" y2="48" stroke={stroke} strokeWidth="1.5" />
    <line x1="12" y1="20" x2="30" y2="6" stroke={stroke} strokeWidth="1.5" />
    <line x1="48" y1="20" x2="30" y2="6" stroke={stroke} strokeWidth="1.5" />
    {/* Knopen */}
    <circle cx="30" cy="6" r="5" fill={stroke} />
    <circle cx="12" cy="20" r="5" fill={stroke} />
    <circle cx="48" cy="20" r="5" fill={stroke} />
    <circle cx="30" cy="48" r="5" fill={stroke} />
  </svg>
);

/** Artifact: rechthoek met "page-fold"-hoek rechtsboven (dog-ear).
 *  De vouw-driehoek staat een paar pixels inset van de wrapper-rand
 *  zodat alle 3 lijnen zichtbaar zijn (hypotenuse + twee perpendicular
 *  edges) — anders vallen 2 lijnen samen met de wrapper-rand en zou
 *  alleen de fold-line tonen. */
const ArtifactDecoration = ({ fill, stroke }: { fill: string; stroke: string }): ReactNode => (
  <svg
    className="pointer-events-none absolute"
    style={{
      top: 3,
      right: 3,
      width: 11,
      height: 11,
    }}
    viewBox="0 0 11 11"
    aria-hidden
  >
    <polygon points="0,0 11,0 11,11" fill={fill} stroke={stroke} strokeWidth="1.2" />
  </svg>
);

/** Equipment: twee tandwielen. */
const EquipmentDecoration = ({ fill, stroke }: { fill: string; stroke: string }): ReactNode => (
  <svg viewBox="0 0 80 60" style={ICONIC_SVG_STYLE} aria-hidden preserveAspectRatio="xMidYMid meet">
    {/* Groot tandwiel */}
    <g transform="translate(30 30)">
      {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
        <rect
          key={angle}
          x="-3"
          y="-22"
          width="6"
          height="6"
          fill={fill}
          stroke={stroke}
          strokeWidth="1.5"
          transform={`rotate(${angle})`}
        />
      ))}
      <circle cx="0" cy="0" r="14" fill={fill} stroke={stroke} strokeWidth="1.5" />
      <circle cx="0" cy="0" r="5" fill={stroke} />
    </g>
    {/* Klein tandwiel */}
    <g transform="translate(60 22)">
      {[0, 60, 120, 180, 240, 300].map((angle) => (
        <rect
          key={angle}
          x="-2"
          y="-14"
          width="4"
          height="4"
          fill={fill}
          stroke={stroke}
          strokeWidth="1.5"
          transform={`rotate(${angle})`}
        />
      ))}
      <circle cx="0" cy="0" r="9" fill={fill} stroke={stroke} strokeWidth="1.5" />
      <circle cx="0" cy="0" r="3" fill={stroke} />
    </g>
  </svg>
);

/** Facility: zigzag-fabrieksdak (factory-roof). Decoration aan de
 *  bovenkant van een rechthoek. */
const FacilityDecoration = ({ fill, stroke }: { fill: string; stroke: string }): ReactNode => (
  <svg
    className="pointer-events-none absolute"
    style={{ top: -10, left: "10%", right: "10%", width: "80%", height: 12 }}
    viewBox="0 0 60 12"
    preserveAspectRatio="none"
    aria-hidden
  >
    <polygon
      points="0,12 0,4 10,12 10,4 20,12 20,4 30,12 30,4 40,12 40,4 50,12 50,4 60,12"
      fill={fill}
      stroke={stroke}
      strokeWidth="1.5"
    />
  </svg>
);

/** DistributionNetwork: horizontale dubbele-kop pijl. */
const DistributionNetworkDecoration = ({ stroke }: { fill: string; stroke: string }): ReactNode => (
  <svg viewBox="0 0 80 30" style={ICONIC_SVG_STYLE} aria-hidden preserveAspectRatio="xMidYMid meet">
    <line x1="14" y1="15" x2="66" y2="15" stroke={stroke} strokeWidth="2.5" />
    <polyline points="20,7 8,15 20,23" fill="none" stroke={stroke} strokeWidth="2.5" />
    <polyline points="60,7 72,15 60,23" fill="none" stroke={stroke} strokeWidth="2.5" />
  </svg>
);

/* ──────────────────────── Generic iconic shapes ───────────────────── */

/** Grouping: tweede dashed-rectangle offset achter de wrapper, geeft
 *  het "stacked"-effect van twee overlappende groeperingen. Hoofd-
 *  wrapper rendert al z'n eigen dashed border via `dashedBorder`. */
const GroupingDecoration = ({ stroke }: { fill: string; stroke: string }): ReactNode => (
  <div
    className="pointer-events-none absolute"
    style={{
      top: -6,
      left: 6,
      right: -6,
      bottom: 6,
      border: `1.5px dashed ${stroke}`,
      backgroundColor: "transparent",
      zIndex: -1,
    }}
    aria-hidden
  />
);

/** Location: map-pin / teardrop — afgeronde top met punt naar beneden. */
const LocationDecoration = ({ fill, stroke }: { fill: string; stroke: string }): ReactNode => (
  <svg viewBox="0 0 60 80" style={ICONIC_SVG_STYLE} aria-hidden preserveAspectRatio="xMidYMid meet">
    <path d="M 30 78 L 8 36 A 22 22 0 1 1 52 36 Z" fill={fill} stroke={stroke} strokeWidth="2" />
    <circle cx="30" cy="28" r="6" fill={stroke} />
  </svg>
);

/* ──────────────────────── Implementation iconic shapes ────────────── */

/** WorkPackage: bijna-volledige circulaire pijl die loopt van top
 *  klokmee rond tot rechts-onder, eindigend in een arrowhead. */
const WorkPackageDecoration = ({ stroke }: { fill: string; stroke: string }): ReactNode => (
  <svg viewBox="0 0 60 60" style={ICONIC_SVG_STYLE} aria-hidden preserveAspectRatio="xMidYMid meet">
    {/* ~290° arc clockwise: van top (30,6) naar rechts-onder (50,44) */}
    <path
      d="M 30 6 A 24 24 0 1 1 50 44"
      fill="none"
      stroke={stroke}
      strokeWidth="5"
      strokeLinecap="round"
    />
    {/* Arrowhead aan het einde — wijst tangentieel naar rechts-onder. */}
    <polygon points="42,32 56,42 42,54" fill={stroke} />
  </svg>
);

/** Plateau: 3 horizontale balken cascade-stijl, elk shift naar rechts
 *  t.o.v. de balk eronder — geeft "trap omhoog naar rechts"-effect. */
const PlateauDecoration = ({ stroke }: { fill: string; stroke: string }): ReactNode => (
  <svg viewBox="0 0 80 60" style={ICONIC_SVG_STYLE} aria-hidden preserveAspectRatio="xMidYMid meet">
    <rect x="30" y="8" width="40" height="8" fill={stroke} />
    <rect x="18" y="24" width="40" height="8" fill={stroke} />
    <rect x="6" y="40" width="40" height="8" fill={stroke} />
  </svg>
);

/** Gap: cirkel met TWEE parallelle horizontale strepen erdoorheen
 *  (boven en onder 't midden — geeft 't "gap"-effect, niet doorgekruist). */
const GapDecoration = ({ fill, stroke }: { fill: string; stroke: string }): ReactNode => (
  <svg viewBox="0 0 60 60" style={ICONIC_SVG_STYLE} aria-hidden preserveAspectRatio="xMidYMid meet">
    <circle cx="30" cy="30" r="22" fill={fill} stroke={stroke} strokeWidth="2" />
    <line x1="2" y1="22" x2="58" y2="22" stroke={stroke} strokeWidth="2.5" />
    <line x1="2" y1="38" x2="58" y2="38" stroke={stroke} strokeWidth="2.5" />
  </svg>
);

/* ──────────────────────── Motivation iconic shapes ────────────────── */

/** Driver: scheepswiel / kompas-roos met 8 spokes door 't midden,
 *  dark center disc. */
const DriverDecoration = ({ fill, stroke }: { fill: string; stroke: string }): ReactNode => (
  <svg viewBox="0 0 60 60" style={ICONIC_SVG_STYLE} aria-hidden preserveAspectRatio="xMidYMid meet">
    <circle cx="30" cy="30" r="26" fill={fill} stroke={stroke} strokeWidth="2.5" />
    {/* 8 spokes door middel — horizontaal, verticaal, 2 diagonalen */}
    {[0, 45, 90, 135].map((deg) => (
      <line
        key={deg}
        x1="30"
        y1="4"
        x2="30"
        y2="56"
        stroke={stroke}
        strokeWidth="2"
        transform={`rotate(${deg} 30 30)`}
      />
    ))}
    {/* Filled center hub */}
    <circle cx="30" cy="30" r="7" fill={stroke} />
  </svg>
);

/** Assessment: vergrootglas (cirkel + handvat). */
const AssessmentDecoration = ({ fill, stroke }: { fill: string; stroke: string }): ReactNode => (
  <svg viewBox="0 0 60 60" style={ICONIC_SVG_STYLE} aria-hidden preserveAspectRatio="xMidYMid meet">
    <circle cx="24" cy="24" r="18" fill={fill} stroke={stroke} strokeWidth="2.5" />
    <line x1="37" y1="37" x2="54" y2="54" stroke={stroke} strokeWidth="4" strokeLinecap="round" />
  </svg>
);

/** Goal: bullseye met vol-gevulde centrum. */
const GoalDecoration = ({ fill, stroke }: { fill: string; stroke: string }): ReactNode => (
  <svg viewBox="0 0 60 60" style={ICONIC_SVG_STYLE} aria-hidden preserveAspectRatio="xMidYMid meet">
    <circle cx="30" cy="30" r="26" fill={fill} stroke={stroke} strokeWidth="2" />
    <circle cx="30" cy="30" r="16" fill={fill} stroke={stroke} strokeWidth="2" />
    <circle cx="30" cy="30" r="9" fill={stroke} />
  </svg>
);

/** Outcome: bullseye met alternating dark/light ringen + diagonaal-pijl
 *  die de roos raakt (vanuit rechts-boven). */
const OutcomeDecoration = ({ fill, stroke }: { fill: string; stroke: string }): ReactNode => (
  <svg viewBox="0 0 60 60" style={ICONIC_SVG_STYLE} aria-hidden preserveAspectRatio="xMidYMid meet">
    <circle cx="28" cy="32" r="22" fill={stroke} />
    <circle cx="28" cy="32" r="17" fill={fill} />
    <circle cx="28" cy="32" r="12" fill={stroke} />
    <circle cx="28" cy="32" r="7" fill={fill} />
    <circle cx="28" cy="32" r="3" fill={stroke} />
    {/* Pijl-schacht, dik en strak */}
    <line x1="54" y1="6" x2="32" y2="28" stroke={stroke} strokeWidth="3.5" strokeLinecap="round" />
    {/* Pijlpunt — duidelijke V naar de roos */}
    <polyline
      points="44,5 54,6 53,16"
      fill={stroke}
      stroke={stroke}
      strokeWidth="2.5"
      strokeLinejoin="round"
    />
  </svg>
);

/** Principle: uitroepteken in afgeronde vierkant. */
const PrincipleDecoration = ({ fill, stroke }: { fill: string; stroke: string }): ReactNode => (
  <svg viewBox="0 0 60 60" style={ICONIC_SVG_STYLE} aria-hidden preserveAspectRatio="xMidYMid meet">
    <rect x="6" y="6" width="48" height="48" rx="8" fill={fill} stroke={stroke} strokeWidth="3" />
    <line x1="30" y1="14" x2="30" y2="36" stroke={stroke} strokeWidth="6" strokeLinecap="round" />
    <circle cx="30" cy="46" r="3" fill={stroke} />
  </svg>
);

/** Meaning: thought-bubble (cloud) — gestileerde cloud-shape. */
const MeaningDecoration = ({ fill, stroke }: { fill: string; stroke: string }): ReactNode => (
  <svg viewBox="0 0 80 60" style={ICONIC_SVG_STYLE} aria-hidden preserveAspectRatio="xMidYMid meet">
    <path
      d="M 16 36 Q 4 36 8 26 Q 0 18 12 16 Q 16 4 32 10 Q 44 0 56 12 Q 72 8 70 24 Q 80 32 64 36 Q 60 48 44 44 Q 32 52 22 44 Q 12 48 16 36 Z"
      fill={fill}
      stroke={stroke}
      strokeWidth="2"
    />
  </svg>
);

/** Parallelogram-shapes voor Requirement / Constraint. */
const REQUIREMENT_PARALLELOGRAM = polyShape([
  [12, 0],
  [100, 0],
  [88, 100],
  [0, 100],
]);

/** Constraint: parallelogram met TWEE interne diagonale lijnen parallel
 *  aan de schuine zijden — divides parallelogram in 3 secties. */
const ConstraintDecoration = ({ stroke }: { fill: string; stroke: string }): ReactNode => (
  <svg
    className="pointer-events-none absolute inset-0"
    viewBox="0 0 100 100"
    preserveAspectRatio="none"
    aria-hidden
  >
    {/* Twee parallelle diagonale lijnen parallel aan de parallelogram-
        schuinte (12% afwijking — matched met REQUIREMENT_PARALLELOGRAM). */}
    <line
      x1="36"
      y1="0"
      x2="24"
      y2="100"
      stroke={stroke}
      strokeWidth="1.5"
      vectorEffect="non-scaling-stroke"
    />
    <line
      x1="64"
      y1="0"
      x2="52"
      y2="100"
      stroke={stroke}
      strokeWidth="1.5"
      vectorEffect="non-scaling-stroke"
    />
  </svg>
);

/* ──────────────────────── Strategy iconic shapes ──────────────────── */

/** Resource: batterij — horizontale rechthoek met 3 verticale streepjes
 *  en een kleine "tip" rechts (batterij-pool). */
const ResourceDecoration = ({ fill, stroke }: { fill: string; stroke: string }): ReactNode => (
  <svg viewBox="0 0 80 40" style={ICONIC_SVG_STYLE} aria-hidden preserveAspectRatio="xMidYMid meet">
    {/* Body */}
    <rect x="6" y="8" width="60" height="24" fill={fill} stroke={stroke} strokeWidth="2" />
    {/* Pool rechts */}
    <rect x="66" y="14" width="6" height="12" fill={stroke} />
    {/* 3 verticale streepjes */}
    <line x1="20" y1="14" x2="20" y2="26" stroke={stroke} strokeWidth="2" />
    <line x1="32" y1="14" x2="32" y2="26" stroke={stroke} strokeWidth="2" />
    <line x1="44" y1="14" x2="44" y2="26" stroke={stroke} strokeWidth="2" />
  </svg>
);

/** Capability: 4 blokken in een stair-step / diagonal patroon. */
const CapabilityDecoration = ({ fill, stroke }: { fill: string; stroke: string }): ReactNode => (
  <svg viewBox="0 0 60 60" style={ICONIC_SVG_STYLE} aria-hidden preserveAspectRatio="xMidYMid meet">
    {/* Bottom row */}
    <rect x="4" y="32" width="16" height="16" fill={fill} stroke={stroke} strokeWidth="2" />
    <rect x="20" y="32" width="16" height="16" fill={fill} stroke={stroke} strokeWidth="2" />
    {/* Top row (offset right) */}
    <rect x="20" y="16" width="16" height="16" fill={fill} stroke={stroke} strokeWidth="2" />
    <rect x="36" y="16" width="16" height="16" fill={fill} stroke={stroke} strokeWidth="2" />
  </svg>
);

/** CourseOfAction: bullseye-target met afwisselend dark/light ringen. */
const CourseOfActionDecoration = ({
  fill,
  stroke,
}: {
  fill: string;
  stroke: string;
}): ReactNode => (
  <svg viewBox="0 0 60 60" style={ICONIC_SVG_STYLE} aria-hidden preserveAspectRatio="xMidYMid meet">
    <circle cx="30" cy="30" r="26" fill={stroke} />
    <circle cx="30" cy="30" r="20" fill={fill} />
    <circle cx="30" cy="30" r="14" fill={stroke} />
    <circle cx="30" cy="30" r="8" fill={fill} />
    <circle cx="30" cy="30" r="3" fill={stroke} />
  </svg>
);

/** Material: hexagon clip-path (regulier zes-zijdig). */
const MATERIAL_HEX = polyShape([
  [50, 0],
  [100, 25],
  [100, 75],
  [50, 100],
  [0, 75],
  [0, 25],
]);

/** ApplicationComponent: rechthoek met twee kleine connector-rechthoekjes
 *  aan de linkerkant (poorten/sockets). */
const ApplicationComponentDecoration = ({
  fill,
  stroke,
}: {
  fill: string;
  stroke: string;
}): ReactNode => (
  <>
    <div
      className="pointer-events-none absolute"
      style={{
        left: -5,
        top: "25%",
        width: 12,
        height: 6,
        backgroundColor: fill,
        border: `1.5px solid ${stroke}`,
      }}
      aria-hidden
    />
    <div
      className="pointer-events-none absolute"
      style={{
        left: -5,
        top: "60%",
        width: 12,
        height: 6,
        backgroundColor: fill,
        border: `1.5px solid ${stroke}`,
      }}
      aria-hidden
    />
  </>
);

/** Product: rechthoek met kleine "tab"-rechthoek die BOVEN de wrapper
 *  uitsteekt (folder-tab-stijl). Bottom-border weg zodat 'ie naadloos
 *  overgaat in de wrapper-top. */
const ProductDecoration = ({ fill, stroke }: { fill: string; stroke: string }): ReactNode => (
  <div
    className="pointer-events-none absolute"
    style={{
      top: -8,
      left: 8,
      width: 32,
      height: 8,
      backgroundColor: fill,
      border: `1.5px solid ${stroke}`,
      borderBottom: "none",
    }}
    aria-hidden
  />
);

/** Representation: rechthoek met golvende ONDERRAND (scroll-vorm).
 *  Clip-path-polygon met veel punten approximeert een sinusoïde-wave;
 *  het outline-SVG-overlay tekent dezelfde wave als stroke zodat de
 *  golvende rand zichtbaar is. */
const REPRESENTATION_SCROLL = polyShape([
  [0, 0],
  [100, 0],
  [100, 86],
  [92, 100],
  [83, 86],
  [75, 100],
  [67, 86],
  [58, 100],
  [50, 86],
  [42, 100],
  [33, 86],
  [25, 100],
  [17, 86],
  [8, 100],
  [0, 86],
]);

/** Deliverable: rechthoek met scherpe ZIGZAG-onderrand (sawtooth) —
 *  visueel anders dan de smooth Representation-wave. */
const DELIVERABLE_SAWTOOTH = polyShape([
  [0, 0],
  [100, 0],
  [100, 80],
  [90, 100],
  [80, 80],
  [70, 100],
  [60, 80],
  [50, 100],
  [40, 80],
  [30, 100],
  [20, 80],
  [10, 100],
  [0, 80],
]);

/* ──────────────────────── lookup-tabel ─────────────────────────────── */

const SHAPES: Record<string, ArchimateShapeStyle> = {
  "archimate.BusinessProcess": {
    ...PROCESS_ARROW,
    contentPadding: { left: 16, right: 22 },
  },
  "archimate.BusinessFunction": {
    ...FUNCTION_CHEVRON,
    // V-dips op top en bottom — label moet binnen 't midden-vlak vallen.
    contentPadding: { top: 12, bottom: 12 },
  },
  "archimate.BusinessEvent": {
    ...EVENT_PENTAGON,
    contentPadding: { right: 18 },
  },
  "archimate.BusinessInteraction": {
    Decoration: InteractionDecoration,
    iconic: true,
    defaultSize: { width: 56, height: 72 },
  },
  "archimate.BusinessService": {
    // Pill-shape: 50% radius op een rechthoek geeft halve-cirkel-caps.
    borderRadius: 999,
  },
  "archimate.BusinessRole": {
    Decoration: RoleDecoration,
    iconic: true,
    defaultSize: { width: 100, height: 56 },
  },
  "archimate.BusinessActor": {
    Decoration: ActorDecoration,
    iconic: true,
    defaultSize: { width: 44, height: 72 },
  },
  "archimate.BusinessCollaboration": {
    Decoration: CollaborationDecoration,
    iconic: true,
    defaultSize: { width: 80, height: 56 },
  },
  "archimate.BusinessInterface": {
    Decoration: InterfaceDecoration,
    iconic: true,
    defaultSize: { width: 80, height: 48 },
  },
  "archimate.BusinessObject": {
    Decoration: ObjectDecoration,
    contentPadding: { top: 10 },
  },
  "archimate.Contract": {
    Decoration: ContractDecoration,
    contentPadding: { top: 14 },
  },
  "archimate.Representation": {
    ...REPRESENTATION_SCROLL,
    contentPadding: { bottom: 10 },
  },
  "archimate.Product": {
    Decoration: ProductDecoration,
    contentPadding: { top: 12 },
  },

  // ── Application-laag ──────────────────────────────────────────────
  "archimate.ApplicationComponent": {
    Decoration: ApplicationComponentDecoration,
    contentPadding: { left: 10 },
  },
  "archimate.ApplicationCollaboration": {
    Decoration: CollaborationDecoration,
    iconic: true,
    defaultSize: { width: 80, height: 56 },
  },
  "archimate.ApplicationInterface": {
    Decoration: InterfaceDecoration,
    iconic: true,
    defaultSize: { width: 80, height: 48 },
  },
  "archimate.ApplicationFunction": {
    ...FUNCTION_CHEVRON,
    contentPadding: { top: 12, bottom: 12 },
  },
  "archimate.ApplicationInteraction": {
    Decoration: InteractionDecoration,
    iconic: true,
    defaultSize: { width: 56, height: 72 },
  },
  "archimate.ApplicationProcess": {
    ...PROCESS_ARROW,
    contentPadding: { left: 16, right: 22 },
  },
  "archimate.ApplicationEvent": {
    ...EVENT_PENTAGON,
    contentPadding: { right: 18 },
  },
  "archimate.ApplicationService": {
    borderRadius: 999,
  },
  "archimate.DataObject": {
    Decoration: ObjectDecoration,
    contentPadding: { top: 10 },
  },

  // ── Technology-laag ──────────────────────────────────────────────
  "archimate.Node": {
    Decoration: NodeDecoration,
    iconic: true,
    defaultSize: { width: 72, height: 64 },
  },
  "archimate.Device": {
    Decoration: DeviceDecoration,
    iconic: true,
    defaultSize: { width: 72, height: 56 },
  },
  "archimate.SystemSoftware": {
    Decoration: SystemSoftwareDecoration,
    iconic: true,
    defaultSize: { width: 56, height: 72 },
  },
  "archimate.TechnologyCollaboration": {
    Decoration: CollaborationDecoration,
    iconic: true,
    defaultSize: { width: 80, height: 56 },
  },
  "archimate.TechnologyInterface": {
    Decoration: InterfaceDecoration,
    iconic: true,
    defaultSize: { width: 80, height: 48 },
  },
  "archimate.Path": {
    Decoration: PathDecoration,
    iconic: true,
    defaultSize: { width: 80, height: 48 },
  },
  "archimate.CommunicationNetwork": {
    Decoration: CommunicationNetworkDecoration,
    iconic: true,
    defaultSize: { width: 64, height: 72 },
  },
  "archimate.TechnologyProcess": {
    ...PROCESS_ARROW,
    contentPadding: { left: 16, right: 22 },
  },
  "archimate.TechnologyInteraction": {
    Decoration: InteractionDecoration,
    iconic: true,
    defaultSize: { width: 56, height: 72 },
  },
  "archimate.TechnologyEvent": {
    ...EVENT_PENTAGON,
    contentPadding: { right: 18 },
  },
  "archimate.TechnologyService": {
    borderRadius: 999,
  },
  "archimate.Artifact": {
    Decoration: ArtifactDecoration,
    contentPadding: { top: 14, right: 14 },
  },
  "archimate.Equipment": {
    Decoration: EquipmentDecoration,
    iconic: true,
    defaultSize: { width: 80, height: 64 },
  },
  "archimate.Facility": {
    Decoration: FacilityDecoration,
    contentPadding: { top: 12 },
  },
  "archimate.DistributionNetwork": {
    Decoration: DistributionNetworkDecoration,
    iconic: true,
    defaultSize: { width: 80, height: 48 },
  },
  "archimate.Material": {
    ...MATERIAL_HEX,
    contentPadding: { left: 14, right: 14 },
  },

  // ── Strategy-laag ────────────────────────────────────────────────
  "archimate.Resource": {
    Decoration: ResourceDecoration,
    iconic: true,
    defaultSize: { width: 80, height: 56 },
  },
  "archimate.Capability": {
    Decoration: CapabilityDecoration,
    iconic: true,
    defaultSize: { width: 72, height: 72 },
  },
  "archimate.ValueStream": {
    ...VALUE_STREAM_CHEVRON,
    contentPadding: { left: 18, right: 22 },
  },
  "archimate.CourseOfAction": {
    Decoration: CourseOfActionDecoration,
    iconic: true,
    defaultSize: { width: 64, height: 72 },
  },

  // ── Motivation-laag ─────────────────────────────────────────────
  "archimate.Stakeholder": {
    // Stakeholder = ArchiMate-Riskman-style "rol-cilinder" — zelfde
    // decoratie als BusinessRole.
    Decoration: RoleDecoration,
    iconic: true,
    defaultSize: { width: 100, height: 56 },
  },
  "archimate.Driver": {
    Decoration: DriverDecoration,
    iconic: true,
    defaultSize: { width: 64, height: 72 },
  },
  "archimate.Assessment": {
    Decoration: AssessmentDecoration,
    iconic: true,
    defaultSize: { width: 64, height: 72 },
  },
  "archimate.Goal": {
    Decoration: GoalDecoration,
    iconic: true,
    defaultSize: { width: 64, height: 72 },
  },
  "archimate.Outcome": {
    Decoration: OutcomeDecoration,
    iconic: true,
    defaultSize: { width: 72, height: 72 },
  },
  "archimate.Principle": {
    Decoration: PrincipleDecoration,
    iconic: true,
    defaultSize: { width: 64, height: 72 },
  },
  "archimate.Requirement": {
    ...REQUIREMENT_PARALLELOGRAM,
    contentPadding: { left: 12 },
  },
  "archimate.Constraint": {
    ...REQUIREMENT_PARALLELOGRAM,
    Decoration: ConstraintDecoration,
    contentPadding: { left: 12 },
  },
  "archimate.Meaning": {
    Decoration: MeaningDecoration,
    iconic: true,
    defaultSize: { width: 88, height: 64 },
  },
  "archimate.Value": {
    // 50%-radius op rechthoek = echte ellipse-vorm (oval).
    borderRadius: "50%",
  },

  // ── Implementation & Migration-laag ─────────────────────────────
  "archimate.WorkPackage": {
    Decoration: WorkPackageDecoration,
    iconic: true,
    defaultSize: { width: 64, height: 72 },
  },
  "archimate.Deliverable": {
    ...DELIVERABLE_SAWTOOTH,
    contentPadding: { bottom: 14 },
  },
  "archimate.ImplementationEvent": {
    ...EVENT_PENTAGON,
    contentPadding: { right: 18 },
  },
  "archimate.Plateau": {
    Decoration: PlateauDecoration,
    iconic: true,
    defaultSize: { width: 88, height: 56 },
  },
  "archimate.Gap": {
    Decoration: GapDecoration,
    iconic: true,
    defaultSize: { width: 64, height: 72 },
  },
  // Program is een onze archimate-pack-uitbreiding (PMO/portfolio).
  // Geen iconic — SVG vult de wrapper, label vloeit ergens onder.
  "archimate.Program": {
    iconic: true,
    defaultSize: { width: 80, height: 64 },
  },

  // ── Extension-types (niet in spec, eigen pack-uitbreidingen) ──
  "archimate.Metric": {
    iconic: true,
    defaultSize: { width: 80, height: 64 },
  },
  "archimate.Guideline": {
    iconic: true,
    defaultSize: { width: 80, height: 64 },
  },
  "archimate.UseCase": {
    iconic: true,
    defaultSize: { width: 80, height: 64 },
  },
  "archimate.SecurityDomain": {
    iconic: true,
    defaultSize: { width: 80, height: 64 },
  },

  // ── Generic-laag ───────────────────────────────────────────────
  "archimate.Grouping": {
    dashedBorder: true,
    Decoration: GroupingDecoration,
  },
  "archimate.Location": {
    Decoration: LocationDecoration,
    iconic: true,
    defaultSize: { width: 56, height: 80 },
  },

  // ── Common Domain (ArchiMate 4) ─────────────────────────────────
  // De laag-gebonden 3.1-varianten hadden per aspect al identieke
  // symbolen (Business- en ApplicationProcess deelden beide de
  // pijl-vorm), dus de generieke types erven exact dezelfde style.
  // Daarom is er GEEN compat-stereotype-override nodig: de symbol-vorm
  // verschilt niet tussen 3.1- en 4-weergave, alleen de kleur (die via
  // notation-tokens loopt) en het label.
  "archimate.Process": {
    ...PROCESS_ARROW,
    contentPadding: { left: 16, right: 22 },
  },
  "archimate.Function": {
    ...FUNCTION_CHEVRON,
    contentPadding: { top: 12, bottom: 12 },
  },
  "archimate.Service": {
    borderRadius: 999,
  },
  "archimate.Event": {
    ...EVENT_PENTAGON,
    contentPadding: { right: 18 },
  },
  "archimate.Collaboration": {
    Decoration: CollaborationDecoration,
    iconic: true,
    defaultSize: { width: 80, height: 56 },
  },
  "archimate.Role": {
    Decoration: RoleDecoration,
    iconic: true,
    defaultSize: { width: 100, height: 56 },
  },
};

/** Vraag de ArchiMate-symbol-style op voor een gegeven base-type-id.
 *  Returns `null` wanneer geen mapping bestaat — caller valt dan terug
 *  op de rechthoek-default. */
export function getArchimateShape(typeId: string): ArchimateShapeStyle | null {
  return SHAPES[typeId] ?? null;
}

/** True wanneer ConceptNode dit concept als ArchiMate-symbol mag
 *  renderen: notation_style attribuut staat op "archimate", en er is
 *  een shape-mapping (of een SVG-icoon voor het base-type of stereotype).
 *  Stereotypen worden nu wel toegestaan — risk-Risk / sharp-Concept /
 *  c4-Container hebben hun eigen SVG-icoon, dus geen vorm-conflict. */
export function shouldRenderArchimateShape(
  typeId: string,
  attributes: Record<string, unknown> | undefined,
  _stereotypes: readonly string[] | undefined,
): boolean {
  if (attributes?.notation_style !== "archimate") return false;
  return getArchimateShape(typeId) !== null;
}
